import useOverlay from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import EventListPopup from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import useFilterStore from "@COMPONENTS/product/pages/localList/common/filterHook";
import * as style from "@COMPONENTS/style/productlist/productlist";
import {TextButton} from "@FRAMEWORK/ui/desktop/button";
import {RatingHeader} from "@FRAMEWORK/ui/desktop/filter";
import dayjs from "dayjs";
import {WarningBox} from "@FRAMEWORK/ui/desktop/layout";
import LoadingBox from "@FRAMEWORK/ui/desktop/layout/LoadingBox";
import useGoodsByMenu from "@QUERIES/prdt/goods/useGoodsByMenu";
import {goodsCompareAsync} from "@QUERIES/prdt/goods/useGoodsCompare";
import {GoodsAvailEvent, goodsAvailEventAsync} from "@QUERIES/prdt/goods/useGoodsOneAvailEvent";
import {useRouter} from "next/router";
import React, {useEffect, useState} from "react";
import LocalSortFilter from "../filter/LocalSortFilter";
import CompareProductsPopup from "../popup/CompareProductsPopup";
import LocalGoods from "./LocalGoods";

const LocalGoodsListComponent = () => {
	const router = useRouter();
	const dspSid = router.query?.dspSid as string;
	// const [showViewBraze, setShowViewBraze] = useState(true);

	const {getFilter, getFilteringProduct} = useFilterStore();
	const [open] = useOverlay();

	const openAlert = (text: string, afterFunc?: () => void) => {
		open(prop => <Alert {...prop} text={text} afterClose={afterFunc} afterConfirm={afterFunc} />);
	};

	const goodsListFetch = useGoodsByMenu(dspSid);
	const goodsList = getFilteringProduct();
	const [checkedArray, setCheckedArray] = useState<Array<string>>([]);

	// 선택상품 비교
	const handleCompare = async () => {
		if (checkedArray.length < 2 || checkedArray.length > 3) {
			openAlert("상품비교를 위해서는 상품을<br/>2개 이상 3개 이하로 선택하셔야 합니다.");
			return false;
		}

		const result = await goodsCompareAsync(String(checkedArray));
		const {data} = result;
		const {body} = data;
		if (body) {
			// setShowViewBraze(false);
			const calendarList = body.calendarList;
			const compareList = body.compareList;
			open(props => <CompareProductsPopup {...props} calendarList={calendarList} compareList={compareList} />);
		}
	};

	const handleChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		// value가 없을 경우 초기화
		if (!value) {
			setCheckedArray([]);
			return;
		}

		const isChecked = checkedArray.includes(value);
		if (checkedArray.length >= 3 && !isChecked) {
			openAlert("비교상품은 총 3개까지 선택이 가능합니다.");
			return;
		}

		if (checkedArray.length < 3 || isChecked) {
			if (checked) {
				setCheckedArray([...checkedArray, value]);
			} else {
				setCheckedArray(checkedArray.filter(v => v !== value));
			}
		}
	};

	useEffect(() => {
		setCheckedArray([]); // 필터 변경시 상품비교 초기화
	}, [getFilter()]);

	const routerGoodsCd = router.query?.goodsCd as string;
	const routerGoodsMonth = router.query?.goodsMonth as string;
	const [isEvent, setIsEvent] = useState(true);
	const [slide] = useOverlay("slideIn");

	useEffect(() => {
		if (isEvent && routerGoodsCd && goodsList.length > 0) {
			const handleEventList = (goodsCd: string, availEvent: GoodsAvailEvent) => {
				setIsEvent(false);
				const productItem = goodsList.find(v => v.goodsCd === goodsCd);
				productItem &&
					slide(props => <EventListPopup {...props} availEvent={availEvent} goodsCd={productItem?.goodsCd} />);
			};
			(async () => {
				const result = await goodsAvailEventAsync(routerGoodsCd);
				const {data} = result;
				const {body} = data;
				if (routerGoodsMonth && dayjs(routerGoodsMonth).isValid()) body.evStartDt = routerGoodsMonth;
				body.evCd && handleEventList(body.evCd.substring(0, 7), body);
			})();
		}
	}, [routerGoodsCd, routerGoodsMonth, goodsList]);

	const renderGoodsList = () => {
		if (goodsListFetch.isFetching) {
			return (
				<>
					<LoadingBox />
				</>
			);
		}

		if (goodsList.length === 0) {
			return <WarningBox>해당하는 상품이 존재하지 않습니다.</WarningBox>;
		}

		return (
			<>
				{goodsList.map(item => (
					<LocalGoods
						product={item}
						key={item.goodsCd}
						checkedArray={checkedArray}
						handleChecked={handleChecked}
						handleCompare={handleCompare}
					/>
				))}
			</>
		);
	};

	return (
		<style.ProcutContentWrap>
			<style.ProcutContentWrap>
				{/* sortFiler */}
				<RatingHeader>
					<TextButton text="선택상품 비교" size="default" onClick={handleCompare} />
					{/* sort */}
					<LocalSortFilter />
				</RatingHeader>

				{/* productList */}
				{renderGoodsList()}
			</style.ProcutContentWrap>
		</style.ProcutContentWrap>
	);
};

const LocalGoodsList = () => (
	<ErrorBoundary logMsg="ProductArea Error">
		<LocalGoodsListComponent />
	</ErrorBoundary>
);

export default LocalGoodsList;
